/**
 * Base debug system implementation.
 * This provides core functionality that is shared between ClientDebugSystem and ServerDebugSystem.
 */

import { System } from '../base/System.js';
import {
  type DebugLogEntry,
  type SystemDebugInfo,
  type EntityDebugInfo,
  type SystemDebugUpdateOptions,
  type EntityDebugUpdateOptions,
  type IDebugSystem,
  type DebugLogger,
  type IDebugLogger,
  DEBUG_SYSTEM_NAME,
  NOOP_DEBUG_LOGGER,
} from './types.js';

/**
 * API surface that the debug system exposes via $api on SharedApiRegistry.
 * Every system (client or server) can access these via this.$api.
 */
export type DebugSystemApi = {
  createSystemLogger: (systemName: string) => DebugLogger;
  createEntityLogger: (entityId: string | number, entityType: string) => DebugLogger;
  updateSystemDebugInfo: (
    systemName: string,
    info: Partial<Omit<SystemDebugInfo, 'name' | 'logs'>>,
    options?: SystemDebugUpdateOptions
  ) => void;
  updateEntityDebugInfo: (
    entityId: string | number,
    info: Partial<Omit<EntityDebugInfo, 'id' | 'logs'>>,
    options?: EntityDebugUpdateOptions
  ) => void;
  getSystemDebugInfo: (systemName: string) => SystemDebugInfo | undefined;
  getEntityDebugInfo: (entityId: string | number) => EntityDebugInfo | undefined;
  getAllSystemDebugInfo: () => Record<string, SystemDebugInfo>;
  getAllEntityDebugInfo: () => Record<string, EntityDebugInfo>;
  isDebugEnabled: () => boolean;
};

declare module '../base/System.js' {
  interface SharedApiRegistry extends DebugSystemApi {}
}

/**
 * Generate a unique ID for log entries.
 */
function generateLogId(): string {
  return Math.random().toString(36).substring(2, 11);
}

/**
 * Create a debug logger implementation.
 */
function createDebugLoggerImpl(
  systemName: string,
  source: 'client' | 'server',
  addLogEntry: (entry: DebugLogEntry) => void,
  getAreaMeta?: () => { areaId?: string; areaType?: string },
  groupId?: string
): DebugLogger {
  const normalizedName = systemName.replace(/:{2,}$/, '');

  function createEntry(level: 'log' | 'info' | 'warn' | 'error', args: any[]): DebugLogEntry {
    const meta = getAreaMeta?.() ?? {};
    return {
      id: generateLogId(),
      timestamp: Date.now(),
      level,
      systemName: normalizedName,
      args: [...args],
      source,
      areaId: meta.areaId,
      areaType: meta.areaType,
      groupId,
    };
  }

  function log(...args: any[]): void {
    addLogEntry(createEntry('log', args));
  }

  function info(...args: any[]): void {
    addLogEntry(createEntry('info', args));
  }

  function warn(...args: any[]): void {
    addLogEntry(createEntry('warn', args));
  }

  function error(...args: any[]): void {
    addLogEntry(createEntry('error', args));
  }

  function group(newGroupId: string): IDebugLogger {
    if (!newGroupId) {
      throw new Error('debugLogger.group requires a non-empty group id.');
    }
    return createDebugLoggerImpl(normalizedName, source, addLogEntry, getAreaMeta, newGroupId);
  }

  // Create callable function
  const logger = function (...args: any[]) {
    log(...args);
  } as DebugLogger;

  logger.log = log;
  logger.info = info;
  logger.warn = warn;
  logger.error = error;
  logger.group = group;

  return logger;
}

/**
 * Create an entity debug logger implementation.
 */
function createEntityDebugLoggerImpl(
  entityId: string | number,
  entityType: string,
  source: 'client' | 'server',
  addEntityLogEntry: (entry: DebugLogEntry) => void,
  groupId?: string
): DebugLogger {
  function createEntry(level: 'log' | 'info' | 'warn' | 'error', args: any[]): DebugLogEntry {
    return {
      id: generateLogId(),
      timestamp: Date.now(),
      level,
      systemName: `ENTITY:${entityId}`,
      args: [...args],
      source,
      entityId,
      entityType,
      groupId,
    };
  }

  function log(...args: any[]): void {
    addEntityLogEntry(createEntry('log', args));
  }

  function info(...args: any[]): void {
    addEntityLogEntry(createEntry('info', args));
  }

  function warn(...args: any[]): void {
    addEntityLogEntry(createEntry('warn', args));
  }

  function error(...args: any[]): void {
    addEntityLogEntry(createEntry('error', args));
  }

  function group(newGroupId: string): IDebugLogger {
    if (!newGroupId) {
      throw new Error('debugLogger.group requires a non-empty group id.');
    }
    return createEntityDebugLoggerImpl(entityId, entityType, source, addEntityLogEntry, newGroupId);
  }

  const logger = function (...args: any[]) {
    log(...args);
  } as DebugLogger;

  logger.log = log;
  logger.info = info;
  logger.warn = warn;
  logger.error = error;
  logger.group = group;

  return logger;
}

/**
 * Base debug system class that provides common debug functionality.
 * Extended by ClientDebugSystem and ServerDebugSystem.
 */
export abstract class BaseDebugSystem extends System<DebugSystemApi> implements IDebugSystem {
  /** Whether debug is enabled */
  protected _enabled: boolean = true;
  
  /** System debug info storage */
  protected readonly systemDebugInfo: Record<string, SystemDebugInfo> = {};
  
  /** Entity debug info storage */
  protected readonly entityDebugInfo: Record<string, EntityDebugInfo> = {};
  
  /** System logs by system name */
  protected readonly logsBySystem: Record<string, DebugLogEntry[]> = {};
  
  /** Entity logs by entity ID */
  protected readonly logsByEntity: Record<string, DebugLogEntry[]> = {};
  
  /** Grouped log entries for systems (for replace-on-groupId behavior) */
  protected readonly systemGroupLogEntries: Record<string, Record<string, DebugLogEntry>> = {};
  
  /** Grouped log entries for entities */
  protected readonly entityGroupLogEntries: Record<string, Record<string, DebugLogEntry>> = {};
  
  /** Default max logs per system/entity */
  protected maxLogs: number = 100;

  /** Source identifier ('client' or 'server') */
  protected abstract readonly source: 'client' | 'server';

  constructor() {
    super(DEBUG_SYSTEM_NAME);
  }

  get enabled(): boolean {
    return this._enabled;
  }

  set enabled(value: boolean) {
    this._enabled = value;
  }

  /**
   * Get area metadata for log entries (overridden by subclasses).
   */
  protected getAreaMeta(): { areaId?: string; areaType?: string } {
    return {};
  }

  /**
   * Normalize system name by removing trailing colons.
   */
  protected normalizeSystemName(systemName: string): string {
    return String(systemName).replace(/:{2,}$/, '');
  }

  /**
   * Ensure system debug info exists for a given system.
   */
  protected ensureSystemDebugInfo(systemName: string): SystemDebugInfo {
    const normalizedName = this.normalizeSystemName(systemName);
    if (!this.systemDebugInfo[normalizedName]) {
      this.systemDebugInfo[normalizedName] = {
        name: normalizedName,
        logs: [],
        properties: {},
        state: {},
        propertiesBySource: {},
        stateBySource: {},
        maxLogs: this.maxLogs,
        systemType: this.source,
      };
      this.logsBySystem[normalizedName] = [];
    }
    return this.systemDebugInfo[normalizedName];
  }

  /**
   * Ensure entity debug info exists for a given entity.
   */
  protected ensureEntityDebugInfo(entityId: string | number, entityType?: string): EntityDebugInfo {
    const id = String(entityId);
    if (!this.entityDebugInfo[id]) {
      this.entityDebugInfo[id] = {
        id: entityId,
        entityType: entityType || 'Unknown',
        name: `Entity ${entityId}`,
        logs: [],
        properties: {},
        state: {},
        maxLogs: this.maxLogs,
      };
      this.logsByEntity[id] = [];
    }
    return this.entityDebugInfo[id];
  }

  /**
   * Deep clone a value for debug storage (to avoid reference issues).
   */
  protected cloneDebugValue<V>(value: V): V {
    if (value === null || value === undefined) {
      return value;
    }
    if (typeof value !== 'object') {
      return value;
    }
    try {
      return JSON.parse(JSON.stringify(value));
    } catch {
      // If cloning fails, return the original (may have circular refs)
      return value;
    }
  }

  /**
   * Add a log entry for a system.
   */
  addLogEntry(entry: DebugLogEntry): void {
    if (!this._enabled) return;

    const systemName = this.normalizeSystemName(entry.systemName);
    const info = this.ensureSystemDebugInfo(systemName);

    // Handle grouped logs (replace existing log with same groupId)
    if (entry.groupId) {
      if (!this.systemGroupLogEntries[systemName]) {
        this.systemGroupLogEntries[systemName] = {};
      }
      const existing = this.systemGroupLogEntries[systemName][entry.groupId];
      if (existing) {
        // Update existing entry in place
        Object.assign(existing, entry);
        return;
      } else {
        // Add new grouped entry
        this.systemGroupLogEntries[systemName][entry.groupId] = entry;
      }
    }

    // Add to logs array
    info.logs.push(entry);
    if (info.logs.length > info.maxLogs) {
      info.logs.shift();
    }

    // Also maintain the logsBySystem array
    if (!this.logsBySystem[systemName]) {
      this.logsBySystem[systemName] = [];
    }
    this.logsBySystem[systemName].push(entry);
    if (this.logsBySystem[systemName].length > info.maxLogs) {
      this.logsBySystem[systemName].shift();
    }
  }

  /**
   * Add a log entry for an entity.
   */
  addEntityLogEntry(entry: DebugLogEntry): void {
    if (!this._enabled) return;

    const entityId = String(entry.entityId ?? entry.systemName.replace('ENTITY:', ''));
    const info = this.ensureEntityDebugInfo(entityId, entry.entityType);

    // Handle grouped logs
    if (entry.groupId) {
      if (!this.entityGroupLogEntries[entityId]) {
        this.entityGroupLogEntries[entityId] = {};
      }
      const existing = this.entityGroupLogEntries[entityId][entry.groupId];
      if (existing) {
        Object.assign(existing, entry);
        return;
      } else {
        this.entityGroupLogEntries[entityId][entry.groupId] = entry;
      }
    }

    info.logs.push(entry);
    if (info.logs.length > info.maxLogs) {
      info.logs.shift();
    }

    if (!this.logsByEntity[entityId]) {
      this.logsByEntity[entityId] = [];
    }
    this.logsByEntity[entityId].push(entry);
    if (this.logsByEntity[entityId].length > info.maxLogs) {
      this.logsByEntity[entityId].shift();
    }
  }

  /**
   * Update debug info for a system.
   */
  updateSystemDebugInfo(
    systemName: string,
    info: Partial<Omit<SystemDebugInfo, 'name' | 'logs'>>,
    options?: SystemDebugUpdateOptions
  ): void {
    if (!this._enabled) return;

    const normalizedName = this.normalizeSystemName(systemName);
    const existing = this.ensureSystemDebugInfo(normalizedName);

    if (options?.replace) {
      if (info.state !== undefined) {
        existing.state = this.cloneDebugValue(info.state);
      }
      if (info.properties !== undefined) {
        existing.properties = this.cloneDebugValue(info.properties);
      }
    } else {
      // Merge
      if (info.state !== undefined) {
        existing.state = { ...existing.state, ...this.cloneDebugValue(info.state) };
      }
      if (info.properties !== undefined) {
        existing.properties = { ...existing.properties, ...this.cloneDebugValue(info.properties) };
      }
    }

    // Handle by-source state/properties
    if (info.stateBySource) {
      if (!existing.stateBySource) existing.stateBySource = {};
      for (const src of ['client', 'server'] as const) {
        if (info.stateBySource[src] !== undefined) {
          existing.stateBySource[src] = options?.replace
            ? this.cloneDebugValue(info.stateBySource[src])
            : { ...existing.stateBySource[src], ...this.cloneDebugValue(info.stateBySource[src]) };
        }
      }
    }
    if (info.propertiesBySource) {
      if (!existing.propertiesBySource) existing.propertiesBySource = {};
      for (const src of ['client', 'server'] as const) {
        if (info.propertiesBySource[src] !== undefined) {
          existing.propertiesBySource[src] = options?.replace
            ? this.cloneDebugValue(info.propertiesBySource[src])
            : { ...existing.propertiesBySource[src], ...this.cloneDebugValue(info.propertiesBySource[src]) };
        }
      }
    }

    if (info.maxLogs !== undefined) {
      existing.maxLogs = info.maxLogs;
    }
    if (info.systemType !== undefined) {
      existing.systemType = info.systemType;
    }
  }

  /**
   * Update debug info for an entity.
   */
  updateEntityDebugInfo(
    entityId: string | number,
    info: Partial<Omit<EntityDebugInfo, 'id' | 'logs'>>,
    options?: EntityDebugUpdateOptions
  ): void {
    if (!this._enabled) return;

    const existing = this.ensureEntityDebugInfo(entityId, info.entityType);

    if (info.entityType !== undefined) {
      existing.entityType = info.entityType;
    }
    if (info.name !== undefined) {
      existing.name = info.name;
    }

    if (options?.replace) {
      if (info.state !== undefined) {
        existing.state = this.cloneDebugValue(info.state);
      }
      if (info.properties !== undefined) {
        existing.properties = this.cloneDebugValue(info.properties);
      }
    } else {
      if (info.state !== undefined) {
        existing.state = { ...existing.state, ...this.cloneDebugValue(info.state) };
      }
      if (info.properties !== undefined) {
        existing.properties = { ...existing.properties, ...this.cloneDebugValue(info.properties) };
      }
    }

    if (info.maxLogs !== undefined) {
      existing.maxLogs = info.maxLogs;
    }
  }

  /**
   * Get debug info for a system.
   */
  getSystemDebugInfo(systemName: string): SystemDebugInfo | undefined {
    return this.systemDebugInfo[this.normalizeSystemName(systemName)];
  }

  /**
   * Get debug info for an entity.
   */
  getEntityDebugInfo(entityId: string | number): EntityDebugInfo | undefined {
    return this.entityDebugInfo[String(entityId)];
  }

  /**
   * Get all system debug info.
   */
  getAllSystemDebugInfo(): Record<string, SystemDebugInfo> {
    return this.systemDebugInfo;
  }

  /**
   * Get all entity debug info.
   */
  getAllEntityDebugInfo(): Record<string, EntityDebugInfo> {
    return this.entityDebugInfo;
  }

  /**
   * Create a debug logger for a system.
   */
  createSystemLogger(systemName: string): DebugLogger {
    if (!this._enabled) {
      return NOOP_DEBUG_LOGGER;
    }
    return createDebugLoggerImpl(
      systemName,
      this.source,
      this.addLogEntry.bind(this),
      this.getAreaMeta.bind(this)
    );
  }

  /**
   * Create a debug logger for an entity.
   */
  createEntityLogger(entityId: string | number, entityType: string): DebugLogger {
    if (!this._enabled) {
      return NOOP_DEBUG_LOGGER;
    }
    return createEntityDebugLoggerImpl(
      entityId,
      entityType,
      this.source,
      this.addEntityLogEntry.bind(this)
    );
  }

  /**
   * Register all debug API methods on the shared $api.
   * Subclasses that override onInit() must call super.onInit() first.
   */
  override onInit(): void {
    this.addApi('createSystemLogger', this.createSystemLogger.bind(this));
    this.addApi('createEntityLogger', this.createEntityLogger.bind(this));
    this.addApi('updateSystemDebugInfo', this.updateSystemDebugInfo.bind(this));
    this.addApi('updateEntityDebugInfo', this.updateEntityDebugInfo.bind(this));
    this.addApi('getSystemDebugInfo', this.getSystemDebugInfo.bind(this));
    this.addApi('getEntityDebugInfo', this.getEntityDebugInfo.bind(this));
    this.addApi('getAllSystemDebugInfo', this.getAllSystemDebugInfo.bind(this));
    this.addApi('getAllEntityDebugInfo', this.getAllEntityDebugInfo.bind(this));
    this.addApi('isDebugEnabled', () => this._enabled);
  }

  /**
   * Clear all debug info (useful for testing or reset).
   */
  clear(): void {
    for (const key of Object.keys(this.systemDebugInfo)) {
      delete this.systemDebugInfo[key];
    }
    for (const key of Object.keys(this.entityDebugInfo)) {
      delete this.entityDebugInfo[key];
    }
    for (const key of Object.keys(this.logsBySystem)) {
      delete this.logsBySystem[key];
    }
    for (const key of Object.keys(this.logsByEntity)) {
      delete this.logsByEntity[key];
    }
    for (const key of Object.keys(this.systemGroupLogEntries)) {
      delete this.systemGroupLogEntries[key];
    }
    for (const key of Object.keys(this.entityGroupLogEntries)) {
      delete this.entityGroupLogEntries[key];
    }
  }

  /**
   * Remove debug info for a destroyed entity.
   */
  removeEntityDebugInfo(entityId: string | number): void {
    const id = String(entityId);
    delete this.entityDebugInfo[id];
    delete this.logsByEntity[id];
    delete this.entityGroupLogEntries[id];
  }
}

// Re-export types for convenience
export {
  type DebugLogEntry,
  type SystemDebugInfo,
  type EntityDebugInfo,
  type SystemDebugUpdateOptions,
  type EntityDebugUpdateOptions,
  type IDebugSystem,
  type DebugLogger,
  type IDebugLogger,
  type IDebugPageConfig,
  type IDebugPage,
  DEBUG_SYSTEM_NAME,
  DEBUG_MESSAGE_PREFIX,
  DEBUG_MESSAGES,
  NOOP_DEBUG_LOGGER,
} from './types.js';

// DebugSystemApi is exported from the class definition above
